import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { DATE_FNS_LOCALE } from '@hhgtech/hhg-components/constants'
import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Button } from '@hhgtech/hhg-components/mantine'
import { AspectRatio, Box, BoxProps, Flex } from '@mantine/core'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import Share from 'components/atoms/icons/Share'
import TikiArrow from 'components/atoms/icons/TikiArrow'
import Lottie from 'components/atoms/lottie'
import { CountDownTimer } from 'components/molecules/coundownTimer'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ENV, LOCALE_SPECS } from 'interfaces/constants'
import { ScheduleEventCard } from 'interfaces/types'

import { Banner } from './banner'
import useStyles from './index.styles'
import SectionWrapper from '../../sectionWrapper'
import {
  DATE_FORMAT_API,
  ScheduleEventContext,
  ScheduleEventStatus,
} from '../context'
import {
  findNextEvent,
  getUpcommingEvents,
  hasCommingCountdown,
} from '../utils'

export type HighlightEventStatus =
  | 'comming'
  | 'commingWithCountDown'
  | 'live'
  | 'about-lived'
  | 'lived'

const LIVED_STATUS = ['lived', 'about-lived']

export const HighlightEvent = ({ className, styles, classNames }: BoxProps) => {
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const {
    applyH1,
    listEvent,
    openLeadModal,
    setCampaign,
    setHighlightEvent,
  } = useContext(ScheduleEventContext)
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()
  const [audioIcon, setAudioIcon] = useState<any>(null)

  const upcommingEvents = useMemo(() => getUpcommingEvents(listEvent), [])
  const [currentEvent, setCurrentEvent] = useState<{
    event: ScheduleEventCard
    isLive: boolean
    isComming: boolean
    hasCountdownNow: boolean
  }>(
    (() => {
      const event = upcommingEvents?.[0]
      return {
        event,
        isLive: event?.state === ScheduleEventStatus.LIVE,
        isComming: event?.state === ScheduleEventStatus.NOT_YET,
        hasCountdownNow: hasCommingCountdown(event),
      }
    })(),
  )
  const [eventStatus, setEventStatus] = useState<HighlightEventStatus>(
    currentEvent.isLive
      ? 'live'
      : currentEvent.isComming && currentEvent.hasCountdownNow
      ? 'commingWithCountDown'
      : currentEvent.isComming
      ? 'comming'
      : 'lived',
  )
  const { classes, cx } = useStyles(
    {
      isLiveEvent: eventStatus === 'live',
      isEndLive: LIVED_STATUS.includes(eventStatus),
    },
    {
      name: 'ScheduleEvent__HighlightEvent',
      styles,
      classNames,
    },
  )

  useEffect(() => {
    // Dynamically import the animation data to avoid SSR issues
    import('../icon.json').then((data) => {
      setAudioIcon(data.default)
    })
  }, [])

  const handleOnClick = useCallback(
    (event: ScheduleEventCard, status: HighlightEventStatus) => {
      status === 'live' || status === 'lived'
        ? event.ctaUrl && window.open(event.ctaUrl)
        : openLeadModal()
    },
    [],
  )

  const action = useMemo(
    () => (
      <Button
        size="lg"
        variant="primary"
        className="action-btn"
        classNames={{
          root: classes.btn,
        }}
        rightIcon={<TikiArrow size={18} color="#FB8908" />}
        data-event-category="Service Connection"
        data-event-action={
          eventStatus === 'live'
            ? 'Watch Livestream Button'
            : 'Leadgen Livestream Button'
        }
        data-event-label={currentUrl}
        onClick={() => handleOnClick(currentEvent.event, eventStatus)}
      >
        {f({
          id:
            eventStatus === 'live' || eventStatus === 'lived'
              ? currentEvent.event?.ctaTitle || 'common.clickToPlay'
              : currentEvent.event?.leadgenCampaign.ctaTitle ||
                'common.registerToPlayNow',
        })}
      </Button>
    ),
    [eventStatus],
  )

  useEffect(() => {
    if (currentEvent.isLive || currentEvent.isComming)
      setHighlightEvent(currentEvent.event)
  }, [])

  useEffect(() => {
    if (eventStatus === 'comming' || eventStatus === 'commingWithCountDown') {
      fetch(
        ENV.NEXT_PUBLIC_LEAD_API +
          'api/campaign/' +
          String(LOCALE_SPECS[locale].SITE_ID || 1),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          // body: JSON.stringify({
          //   url: leadCurrentUrl,
          //   ga_client_id: getCookie('_ga') || null,
          // }),
        },
      )
        .then((response) => {
          return response.json()
        })
        .then((data: any) => {
          setCampaign(
            (data._data?.campaigns || []).find(
              (i: any) => i.code === currentEvent.event.leadgenCampaign.code,
            ),
          )
        })
    } else if (eventStatus === 'about-lived') {
      const nextEvent = findNextEvent(currentEvent.event, listEvent)
      if (nextEvent) {
        setEventStatus(
          hasCommingCountdown(nextEvent) ? 'commingWithCountDown' : 'comming',
        )
        setCurrentEvent({
          event: nextEvent,
          isLive: nextEvent.state === ScheduleEventStatus.LIVE,
          isComming: nextEvent.state === ScheduleEventStatus.NOT_YET,
          hasCountdownNow: hasCommingCountdown(nextEvent),
        })
      } else {
        setEventStatus('lived')
      }
    }
  }, [eventStatus])

  if (!currentEvent.event) {
    return null
  }

  return (
    <>
      <SectionWrapper
        title=""
        description=""
        styles={(theme) => ({
          root: {
            background: LIVED_STATUS.includes(eventStatus)
              ? theme.colors.neutral[0]
              : '#FFFAED',
            [theme.fn.smallerThan('sm')]: {
              paddingTop: 24,
            },
          },
        })}
      >
        <Box className={cx(className, classes.root)}>
          <Box className={classes.info}>
            <Box className={classes.topInfo}>
              {(eventStatus === 'commingWithCountDown' ||
                eventStatus === 'comming') && (
                <Box
                  component={applyH1 ? 'h1' : 'div'}
                  className={classes.heading}
                >
                  {'Đón xem livestream'}
                </Box>
              )}
              <AspectRatio ratio={512 / 68}>
                <img
                  src={CommonGAssets.getAssetPath('tiki/annouce-bar.png')}
                  alt={currentEvent.event.title}
                  loading="lazy"
                />
                <Flex
                  align={'center'}
                  justify={'center'}
                  sx={{ paddingBottom: 8 }}
                >
                  <Box className={classes.announceText}>
                    {format(
                      dayjs(
                        currentEvent.event?.startDate,
                        'YYYY-MM-DD HH:mm',
                      ).toDate(),
                      'eeee, dd.MM.yyyy',
                      {
                        locale:
                          DATE_FNS_LOCALE[
                            locale as keyof typeof DATE_FNS_LOCALE
                          ],
                      },
                    )}
                  </Box>
                </Flex>
              </AspectRatio>
              <Box
                className={classes.statusBar}
                sx={{
                  position: 'relative',
                }}
              >
                {eventStatus === 'comming' ? (
                  <>
                    <Box className={classes.liveBar}>
                      <Box className={classes.liveText}>
                        {f({
                          id: 'common.livestreamIsComming',
                        })}
                      </Box>
                    </Box>
                    <CountDownTimer
                      // NEXT EVENT IS COMMING BUT TIMER IS NOT DETERMINE YET
                      endDate={dayjs(
                        currentEvent.event.countdownStart ||
                          currentEvent.event.startDate,
                        DATE_FORMAT_API,
                      ).toDate()}
                      onEnd={() => {
                        setEventStatus(
                          currentEvent.event.countdownStart
                            ? 'commingWithCountDown'
                            : 'live',
                        )
                        setHighlightEvent(currentEvent.event)
                      }}
                      styles={{
                        root: {
                          display: 'none',
                        },
                      }}
                    />
                  </>
                ) : eventStatus === 'commingWithCountDown' ? (
                  <CountDownTimer
                    hasUnit
                    endDate={dayjs(
                      currentEvent.event?.startDate,
                      DATE_FORMAT_API,
                    ).toDate()}
                    classNames={{
                      root: classes.timer,
                      item: classes.timerItem,
                      divider: classes.timerDivider,
                      text: classes.timerText,
                      unit: classes.timerUnit,
                    }}
                    onEnd={() => setEventStatus('live')}
                    styles={{
                      root: {
                        height: 62,
                      },
                      heading: {
                        display: 'none',
                      },
                    }}
                  />
                ) : eventStatus === 'live' ? (
                  <>
                    <Box className={classes.liveBar}>
                      {audioIcon && (
                        <Lottie
                          animationData={audioIcon}
                          style={{
                            width: 24,
                            height: 24,
                          }}
                          loop={true}
                        />
                      )}
                      <Box className={classes.liveText}>
                        {f({
                          id: 'common.livestreamIsLive',
                        })}
                      </Box>
                    </Box>
                    <CountDownTimer
                      endDate={dayjs(
                        currentEvent.event.endDate,
                        DATE_FORMAT_API,
                      ).toDate()}
                      onEnd={() => setEventStatus('about-lived')}
                      styles={{
                        root: {
                          display: 'none',
                        },
                      }}
                    />
                  </>
                ) : eventStatus === 'lived' ? (
                  <>
                    <Box className={classes.liveBar}>
                      <Box className={classes.liveText}>
                        {f({
                          id: 'common.ended',
                        })}
                      </Box>
                    </Box>
                  </>
                ) : null}
              </Box>
            </Box>
            <Box className={classes.action}>{action}</Box>
          </Box>
          <Banner
            status={eventStatus}
            event={currentEvent.event}
            handleOnClick={handleOnClick}
            data-event-category="Service Connection"
            data-event-action={
              currentEvent.isLive
                ? 'Watch Livestream Banner'
                : 'Leadgen Livestream Banner'
            }
            data-event-label={currentUrl}
          />
        </Box>
      </SectionWrapper>
      {isMobile && (
        <Box className={classes.sticky}>
          <Button
            size="lg"
            variant="tertiary"
            leftIcon={<Share size={18} isOutline />}
            className={classes.share}
            data-event-category="Service Connection"
            data-event-action="Share Livestream Button"
            data-event-label={currentUrl}
            onClick={() =>
              navigator.share({
                url: currentUrl,
                title: `Đón xem livestream ${dayjs(
                  currentEvent.event?.startDate,
                  'YYYY-MM-DD HH:mm',
                ).format('DD.MM.YYYY')}`,
              })
            }
          />
          {action}
        </Box>
      )}
    </>
  )
}
